import { ensureConfigActivated, firebaseConfiguration } from '@/config/analytics'
import 'firebase/compat/remote-config'
import { getValue, Value } from 'firebase/remote-config'
import { useCallback, useEffect, useState } from 'react'

const remoteConfig = firebaseConfiguration

/**
 * Hook to interact with Firebase Remote Config.
 * Provides functions to get typed values and a ready state.
 */
export function useRemoteConfig() {
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    // This effect ensures the component re-renders once the config is ready.
    ensureConfigActivated().then((success) => {
      if (success && !isReady) {
        setIsReady(true)
      }
    })
  }, [isReady])

  const getRawValue = useCallback(
    (key: string): Value | null => {
      if (!isReady) return null
      // getValue is safe to call before activation, but we wait for `isReady`
      // to ensure we have the latest fetched values.
      return getValue(remoteConfig, key)
    },
    [isReady]
  )

  const getString = useCallback(
    (key: string, defaultValue = ''): string => {
      const value = getRawValue(key)?.asString()
      return value || defaultValue
    },
    [getRawValue]
  )

  const getBoolean = useCallback(
    (key: string, defaultValue = false): boolean => {
      const value = getRawValue(key)
      // asBoolean returns false for non-existent keys.
      return value ? value.asBoolean() : defaultValue
    },
    [getRawValue]
  )

  const getNumber = useCallback(
    (key: string, defaultValue = 0): number => {
      const value = getRawValue(key)
      // asNumber returns 0 if key doesn't exist. Check source to differentiate a real 0.
      if (value && value.getSource() !== 'static') {
        return value.asNumber()
      }
      return defaultValue
    },
    [getRawValue]
  )

  const getJSON = useCallback(
    <T extends object>(key: string, defaultValue: T): T => {
      const stringValue = getString(key, '')
      if (!stringValue) return defaultValue

      try {
        return JSON.parse(stringValue) as T
      } catch (e) {
        console.error(`Remote Config: Failed to parse JSON for key "${key}".`, e)
        return defaultValue
      }
    },
    [getString]
  )

  return { isReady, getString, getBoolean, getNumber, getJSON }
}
